
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Trash2, Edit, Plus, Eye, EyeOff } from "lucide-react";

interface GalleryVideo {
  id: string;
  youtube_url: string;
  title: string | null;
  description: string | null;
  is_active: boolean;
  seo_title: string | null;
  meta_description: string | null;
  slug: string | null;
  keywords: string[] | null;
  image_alt_text: string | null;
  category: string | null;
  target_age_group: string | null;
  publish_date: string | null;
  transcript: string | null;
}

const YouTubeGalleryManager = () => {
  const [videos, setVideos] = useState<GalleryVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    youtube_url: "",
    title: "",
    description: "",
    seo_title: "",
    meta_description: "",
    slug: "",
    keywords: [] as string[],
    image_alt_text: "",
    category: "",
    target_age_group: "",
    publish_date: new Date().toISOString().split('T')[0],
    transcript: ""
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('youtube_gallery')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast({
        title: "Error",
        description: "Failed to fetch videos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingId) {
        const { error } = await supabase
          .from('youtube_gallery')
          .update({
            youtube_url: formData.youtube_url,
            title: formData.title || null,
            description: formData.description || null,
            seo_title: formData.seo_title || null,
            meta_description: formData.meta_description || null,
            slug: formData.slug || null,
            keywords: formData.keywords.length > 0 ? formData.keywords : null,
            image_alt_text: formData.image_alt_text || null,
            category: formData.category || null,
            target_age_group: formData.target_age_group || null,
            publish_date: formData.publish_date || null,
            transcript: formData.transcript || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingId);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Video updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('youtube_gallery')
          .insert({
            youtube_url: formData.youtube_url,
            title: formData.title || null,
            description: formData.description || null,
            seo_title: formData.seo_title || null,
            meta_description: formData.meta_description || null,
            slug: formData.slug || null,
            keywords: formData.keywords.length > 0 ? formData.keywords : null,
            image_alt_text: formData.image_alt_text || null,
            category: formData.category || null,
            target_age_group: formData.target_age_group || null,
            publish_date: formData.publish_date || null,
            transcript: formData.transcript || null,
            is_active: true
          });

        if (error) throw error;
        toast({
          title: "Success",
          description: "Video added to gallery successfully",
        });
      }

      setFormData({ 
        youtube_url: "", 
        title: "", 
        description: "",
        seo_title: "",
        meta_description: "",
        slug: "",
        keywords: [],
        image_alt_text: "",
        category: "",
        target_age_group: "",
        publish_date: new Date().toISOString().split('T')[0],
        transcript: ""
      });
      setEditingId(null);
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast({
        title: "Error",
        description: "Failed to save video",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (video: GalleryVideo) => {
    setFormData({
      youtube_url: video.youtube_url,
      title: video.title || "",
      description: video.description || "",
      seo_title: video.seo_title || "",
      meta_description: video.meta_description || "",
      slug: video.slug || "",
      keywords: video.keywords || [],
      image_alt_text: video.image_alt_text || "",
      category: video.category || "",
      target_age_group: video.target_age_group || "",
      publish_date: video.publish_date || new Date().toISOString().split('T')[0],
      transcript: video.transcript || ""
    });
    setEditingId(video.id);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('youtube_gallery')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({
        title: "Success",
        description: "Video deleted successfully",
      });
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast({
        title: "Error",
        description: "Failed to delete video",
        variant: "destructive",
      });
    }
  };

  const toggleActiveStatus = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('youtube_gallery')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      toast({
        title: "Success",
        description: `Video ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
      });
      fetchVideos();
    } catch (error) {
      console.error('Error toggling video status:', error);
      toast({
        title: "Error",
        description: "Failed to update video status",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {editingId ? "Edit Gallery Video" : "Add Gallery Video"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="youtube_url">YouTube URL *</Label>
                <Input
                  id="youtube_url"
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="title">Video Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Fun learning video for kids"
                />
              </div>

              <div>
                <Label htmlFor="seo_title">SEO Title</Label>
                <Input
                  id="seo_title"
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder="Kids Learning Colors - Fun Educational Video"
                  maxLength={60}
                />
                <p className="text-xs text-muted-foreground mt-1">{formData.seo_title.length}/60 characters</p>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  placeholder="Join us for fun learning! This engaging video helps kids discover colors through playful activities and songs. Perfect for toddlers and early learners."
                  maxLength={160}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground mt-1">{formData.meta_description.length}/160 characters</p>
              </div>

              <div>
                <Label htmlFor="slug">URL Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') })}
                  placeholder="kids-learning-colors-fun-video"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto-formatted: lowercase, dashes only</p>
              </div>

              <div>
                <Label htmlFor="image_alt_text">Image Alt Text</Label>
                <Input
                  id="image_alt_text"
                  value={formData.image_alt_text}
                  onChange={(e) => setFormData({ ...formData, image_alt_text: e.target.value })}
                  placeholder="Colorful learning scene with happy children"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="learning-colors">Learning Colors</SelectItem>
                    <SelectItem value="numbers">Numbers</SelectItem>
                    <SelectItem value="animals">Animals</SelectItem>
                    <SelectItem value="shapes">Shapes</SelectItem>
                    <SelectItem value="stories">Stories</SelectItem>
                    <SelectItem value="songs">Songs</SelectItem>
                    <SelectItem value="alphabet">Alphabet</SelectItem>
                    <SelectItem value="science">Science</SelectItem>
                    <SelectItem value="math">Math</SelectItem>
                    <SelectItem value="geography">Geography</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="target_age_group">Target Age Group</Label>
                <Select 
                  value={formData.target_age_group} 
                  onValueChange={(value) => setFormData({ ...formData, target_age_group: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose age group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toddlers-2-4">Toddlers 2-4</SelectItem>
                    <SelectItem value="kids-4-6">Kids 4-6</SelectItem>
                    <SelectItem value="early-learners-6-8">Early Learners 6-8</SelectItem>
                    <SelectItem value="kids-8-10">Kids 8-10</SelectItem>
                    <SelectItem value="all-ages">All Ages</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="publish_date">Publish Date</Label>
                <Input
                  id="publish_date"
                  type="date"
                  value={formData.publish_date}
                  onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="keywords">Keywords/Tags</Label>
                <Input
                  id="keywords"
                  value={formData.keywords.join(', ')}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k) })}
                  placeholder="kids learning, colors, fun, educational, toddlers, interactive"
                />
                <p className="text-xs text-muted-foreground mt-1">Separate with commas. Aim for 5-8 keywords.</p>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="A wonderful learning adventure that helps children discover..."
                  rows={3}
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="transcript">Transcript (Optional)</Label>
                <Textarea
                  id="transcript"
                  value={formData.transcript}
                  onChange={(e) => setFormData({ ...formData, transcript: e.target.value })}
                  placeholder="Welcome to our colorful world! Today we'll learn about red, blue, green..."
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">If available, provide a brief summary or key phrases from the video.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : editingId ? "Update Video" : "Add Video"}
              </Button>
              {editingId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({ 
                      youtube_url: "", 
                      title: "", 
                      description: "",
                      seo_title: "",
                      meta_description: "",
                      slug: "",
                      keywords: [],
                      image_alt_text: "",
                      category: "",
                      target_age_group: "",
                      publish_date: new Date().toISOString().split('T')[0],
                      transcript: ""
                    });
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gallery Videos ({videos.filter(v => v.is_active).length} active)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {videos.map((video) => (
              <div
                key={video.id}
                className={`p-4 border rounded-lg ${
                  video.is_active ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium">{video.title || "Untitled"}</h4>
                    <p className="text-sm text-gray-600 mt-1">{video.youtube_url}</p>
                    {video.seo_title && (
                      <p className="text-sm text-blue-600 mt-1"><strong>SEO:</strong> {video.seo_title}</p>
                    )}
                    {video.category && (
                      <p className="text-sm text-purple-600 mt-1"><strong>Category:</strong> {video.category}</p>
                    )}
                    {video.target_age_group && (
                      <p className="text-sm text-orange-600 mt-1"><strong>Age Group:</strong> {video.target_age_group}</p>
                    )}
                    {video.description && (
                      <p className="text-sm text-gray-500 mt-1">{video.description}</p>
                    )}
                    {video.keywords && video.keywords.length > 0 && (
                      <p className="text-sm text-green-600 mt-1"><strong>Keywords:</strong> {video.keywords.join(', ')}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          video.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {video.is_active ? "Active" : "Hidden"}
                      </span>
                      {video.publish_date && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                          {new Date(video.publish_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActiveStatus(video.id, video.is_active)}
                    >
                      {video.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(video)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(video.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {videos.length === 0 && (
              <p className="text-gray-500 text-center py-8">No videos added yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default YouTubeGalleryManager;
